import {
  Job,
  JobStatus,
  JobStatusDto,
  Mandatory,
  Quality,
  RenderAndSaveToSiteRequest,
  SaveDestination,
  SaveVideoStatus,
  Source,
} from '@wix/video-maker-api';

import type { CreateGeneralVideoRequest } from '@wix/video-maker-api';
import { Omit } from '../../types/utils';
import toPromise from '../../utils/observableToPromise';
import ErrorNormalizer, { ApiType } from '../ErrorNormalizer';
import { videoMakerApi, videoMakerJobQueue } from './libWrapper';

const videoErrorNormalizer = new ErrorNormalizer(ApiType.VIDEO_MAKER_VIDEO);

type normalizeMediaSourcesFunc = (sources: string[]) => Source[];
const normalizeMediaSources: normalizeMediaSourcesFunc = (sources) =>
  sources.map((url) => ({
    url,
    mandatory: Mandatory.Use,
  }));

type ICreateVideoRequest = Pick<
  CreateGeneralVideoRequest,
  | 'script'
  | 'scriptData'
  | 'business'
  | 'sources'
  | 'themeId'
  | 'trackId'
  | 'userSessionId'
>;

const requestCreatingVideo = (request: ICreateVideoRequest) => {
  return toPromise<Job[]>(videoMakerApi.createVideo(request))
    .then((createdVideoJobs) => createdVideoJobs[0])
    .catch((e) => {
      throw videoErrorNormalizer.normalizeError(e);
    });
};

const resolveCondition = (data: JobStatusDto) =>
  data.status !== JobStatus.INIT && data.status !== JobStatus.PROCESSING;
const waitForVideoCreation = (videoSessionId: number) =>
  toPromise<JobStatusDto>(
    videoMakerJobQueue.addJob(new Job({ videoSession: videoSessionId })),
    resolveCondition,
  )
    .then((data) => {
      if (data.status === JobStatus.ERROR) {
        throw data;
      }

      return data;
    })
    .catch((e) => {
      throw videoErrorNormalizer.normalizeError(e);
    });

async function saveVideoToSiteMedia(
  params: Omit<RenderAndSaveToSiteRequest, 'saveTo'>,
) {
  try {
    const progress = await videoMakerApi
      .saveVideo({
        ...params,
        quality: Quality.SD,
        saveTo: SaveDestination.SiteMedia,
      })
      .toPromise();

    if (progress.status !== SaveVideoStatus.OK) {
      throw progress;
    }

    return videoMakerJobQueue
      .getFileName(new Job({ videoSession: params.videoSessionId }))
      .toPromise();
  } catch (e) {
    throw videoErrorNormalizer.normalizeError(e);
  }
}

export {
  ICreateVideoRequest,
  normalizeMediaSources,
  requestCreatingVideo,
  waitForVideoCreation,
  saveVideoToSiteMedia,
};
